import {
  Box,
  Heading,
  Tabs,
  TabList,
  Button,
  useTab,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import React from 'react';
import { Link, useMatch } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import {
  getDecodedTokenOrThrow,
  useUserAuthOrThrow,
} from '~/contexts/UserAuthProvider';

// Fragments
type NavTabProps = {
  children: React.ReactNode;
  end?: boolean;
  isVisible: boolean;
  to: string;
};
const NavTab = ({ children, to, end = false, isVisible }: NavTabProps) => {
  const match = useMatch({ path: to, end });
  const tabProps = useTab({});
  const styles = useMultiStyleConfig('Tabs', tabProps);
  if (!isVisible) return null;
  return (
    <Button
      __css={styles.tab}
      _active={{ fontWeight: 'bold', borderBottom: '2px' }}
      as={Link}
      isActive={!!match}
      mr={10}
      pl="0"
      pr="0"
      textAlign="center"
      alignSelf="center"
      to={to}
    >
      {children}
    </Button>
  );
};

// Exports
type Props = { children: React.ReactNode };
export const SettingsViewTemplate = ({ children }: Props) => {
  const { user } = useUserAuthOrThrow();
  const userType = getDecodedTokenOrThrow().userType;
  const flags = useFeatureFlags();
  return (
    <>
      <Heading as="h2">Settings</Heading>
      <Tabs variant="line" colorScheme="black">
        <TabList>
          {/* Common settings (borrower + capProvider + valleyBank) */}
          <NavTab to="/settings/my-account" isVisible>
            User Information
          </NavTab>

          {/* Borrower settings */}
          <NavTab
            to="/settings/bank-accounts"
            isVisible={userType === 'BORROWER'}
          >
            Bank Accounts
          </NavTab>
          <NavTab
            to="/settings/bank-integrations"
            isVisible={userType === 'BORROWER'}
          >
            Bank Integrations
          </NavTab>
          <NavTab
            to="/settings/accounting-integrations"
            isVisible={
              // @RAILZ TODO: clean up env controller
              userType === 'BORROWER' && !commonUtils.EnvUtils.isProduction()
            }
          >
            Accounting Integrations
          </NavTab>
          <NavTab
            to="/settings/notifications"
            isVisible={userType === 'BORROWER'}
          >
            Notifications
          </NavTab>
          <NavTab to="/settings/webhooks" isVisible={userType === 'BORROWER'}>
            Webhooks
          </NavTab>
          <NavTab
            to="/settings/assignment-strategy"
            isVisible={userType === 'BORROWER' && !!flags.displayAssignment}
          >
            Assignment Strategy
          </NavTab>

          {/* Capital Provider settings */}
          <NavTab
            to="/settings/credit-facilities"
            isVisible={
              userType === 'CAPITAL_PROVIDER' &&
              user.organization.slug !== 'valleyBank' &&
              !!flags.displayLenderSettingsFacilities
            }
          >
            Facilities
          </NavTab>
        </TabList>
      </Tabs>
      <Box mt="25px" padding="10px">
        {children}
      </Box>
    </>
  );
};
